import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Globe, ArrowLeft, ArrowRight, CheckCircle, Plus, Trash, Sparkle, Flag,
  Question, MapPin, Coins, ChartLineUp,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";

const STEPS = [
  { key: "basics", label: "Basics", icon: Flag },
  { key: "metrics", label: "Pricing & Metrics", icon: ChartLineUp },
  { key: "faqs", label: "FAQs", icon: Question },
  { key: "review", label: "Review & Create", icon: CheckCircle },
];

const EMPTY = {
  // basics
  name: "", slug: "", flag: "🌐",
  currency_code: "USD", currency_symbol: "$",
  capital_city: "", language: "", timezone: "",
  setup_timeline_days: 14,
  tagline: "",
  short_description: "",
  long_description: "",
  tile_image_url: "",
  hero_image_url: "",
  // metrics & pricing
  gov_fees_min_usd: 0, gov_fees_max_usd: 0, gov_fees_note: "",
  corporate_tax_rate: 0, personal_tax_rate: 0,
  foreign_ownership_pct: 100,
  ease_of_doing_business: 5, banking_access: 5, banking_difficulty: 5,
  visa_availability: 5, startup_ecosystem: 5, tech_sector: 5,
  import_export: 5, manufacturing: 5, government_incentives: 5,
  immigration_pathways: 5, overall_rating: 6,
  recommended_industries: [],
  expansion_opportunities: [],
  common_business_types: [],
  requirements_summary: "",
  // faqs
  faqs: [{ q: "", a: "" }],
  // flags
  active: true,
  display_order: 999,
};

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60);

export default function AdminCountryWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const upd = (k, v) => {
    setForm((f) => {
      const next = { ...f, [k]: v };
      // Auto-generate slug from name unless user has typed a custom slug
      if (k === "name" && (!f.slug || f.slug === slugify(f.name))) next.slug = slugify(v);
      return next;
    });
  };

  const next = () => {
    if (step === 0) {
      if (!form.name.trim()) return toast.error("Country name is required");
      if (!form.slug.trim()) return toast.error("Slug is required");
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        faqs: form.faqs.filter((f) => f.q.trim() && f.a.trim()),
      };
      const r = await api.post("/admin/countries/wizard", payload);
      toast.success(`${r.data.country.name} added! ${r.data.services_seeded} services seeded.`);
      navigate(`/countries/${r.data.country.slug}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create country");
    } finally {
      setSubmitting(false);
    }
  };

  const StepIcon = STEPS[step].icon;

  return (
    <div data-testid="admin-country-wizard" className="max-w-3xl mx-auto">
      <div className="mb-6">
        <div className="overline mb-1 flex items-center gap-2">
          <Sparkle size={14} weight="duotone" /> Quick add country
        </div>
        <h2 className="font-display text-2xl text-gos-charcoal">
          Add a new country in 3 steps
        </h2>
        <p className="text-xs text-gos-slate mt-1">
          We&apos;ll create the country, auto-seed empty pricing rows for every service,
          and instantly enable it on /countries, /compare and the SEO URL /open-company-in-{form.slug || "{slug}"}.
        </p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-8" data-testid="wizard-stepper">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const done = i < step;
          const active = i === step;
          return (
            <div key={s.key} className="flex items-center gap-2 flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border ${
                active ? "bg-gos-navy text-white border-gos-navy"
                : done ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-white text-gos-muted border-gos-border"
              }`}>
                <Icon size={14} weight="duotone" />
              </div>
              <div className="text-xs uppercase tracking-widest text-gos-slate whitespace-nowrap">{s.label}</div>
              {i < STEPS.length - 1 && <div className="flex-1 h-px bg-gos-border ml-2" />}
            </div>
          );
        })}
      </div>

      <div className="bg-white border border-gos-border rounded-sm p-6 lg:p-8" data-testid={`wizard-step-${STEPS[step].key}`}>
        <div className="flex items-center gap-2 mb-5">
          <StepIcon size={18} weight="duotone" className="text-gos-navy" />
          <h3 className="font-display text-xl text-gos-charcoal">{STEPS[step].label}</h3>
        </div>

        {step === 0 && <StepBasics form={form} upd={upd} />}
        {step === 1 && <StepMetrics form={form} upd={upd} />}
        {step === 2 && <StepFaqs form={form} setForm={setForm} />}
        {step === 3 && <StepReview form={form} />}
      </div>

      <div className="flex justify-between mt-6">
        <button onClick={back} disabled={step === 0} data-testid="wizard-back"
          className="btn-secondary text-xs !py-2 disabled:opacity-40">
          <ArrowLeft size={14} weight="bold" /> Back
        </button>
        {step < STEPS.length - 1 ? (
          <button onClick={next} data-testid="wizard-next" className="btn-primary text-xs !py-2">
            Next <ArrowRight size={14} weight="bold" />
          </button>
        ) : (
          <button onClick={submit} disabled={submitting} data-testid="wizard-create" className="btn-primary text-xs !py-2">
            {submitting ? "Creating…" : <><CheckCircle size={14} weight="bold" /> Create country</>}
          </button>
        )}
      </div>
    </div>
  );
}

function StepBasics({ form, upd }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Country name *" value={form.name} onChange={(v) => upd("name", v)} testid="wiz-name" placeholder="e.g. Kenya" />
        <Field label="Slug *" value={form.slug} onChange={(v) => upd("slug", v)} testid="wiz-slug" placeholder="auto-generated" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Flag emoji" value={form.flag} onChange={(v) => upd("flag", v)} testid="wiz-flag" placeholder="🇰🇪" />
        <Field label="Currency code" value={form.currency_code} onChange={(v) => upd("currency_code", v.toUpperCase())} testid="wiz-ccy" placeholder="KES" />
        <Field label="Currency symbol" value={form.currency_symbol} onChange={(v) => upd("currency_symbol", v)} testid="wiz-ccy-symbol" placeholder="KSh" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Capital city" value={form.capital_city} onChange={(v) => upd("capital_city", v)} testid="wiz-capital" />
        <Field label="Language(s)" value={form.language} onChange={(v) => upd("language", v)} testid="wiz-language" placeholder="English / Swahili" />
        <Field label="Timezone" value={form.timezone} onChange={(v) => upd("timezone", v)} testid="wiz-tz" placeholder="UTC+3" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <NumField label="Setup timeline (days)" value={form.setup_timeline_days} onChange={(v) => upd("setup_timeline_days", Number(v))} testid="wiz-timeline" />
        <NumField label="Display order" value={form.display_order} onChange={(v) => upd("display_order", Number(v))} testid="wiz-order" />
      </div>
      <Field label="Tagline (one-liner)" value={form.tagline} onChange={(v) => upd("tagline", v)} testid="wiz-tagline" placeholder="East African gateway. EAC market access." />
      <TextArea label="Short description (1–2 sentences, used on cards)" value={form.short_description} onChange={(v) => upd("short_description", v)} testid="wiz-short-desc" rows={2} />
      <TextArea label="Long description (full paragraph for the country page hero)" value={form.long_description} onChange={(v) => upd("long_description", v)} testid="wiz-long-desc" rows={4} />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Tile image URL (card thumbnail)" value={form.tile_image_url} onChange={(v) => upd("tile_image_url", v)} testid="wiz-tile-img" />
        <Field label="Hero image URL (country page banner)" value={form.hero_image_url} onChange={(v) => upd("hero_image_url", v)} testid="wiz-hero-img" />
      </div>
      <label className="flex items-center gap-2 text-sm mt-2">
        <input type="checkbox" checked={form.active} onChange={(e) => upd("active", e.target.checked)} data-testid="wiz-active" />
        Publish immediately (uncheck to save as draft)
      </label>
    </div>
  );
}

function StepMetrics({ form, upd }) {
  return (
    <div className="space-y-5">
      <div>
        <div className="overline mb-2 flex items-center gap-1"><Coins size={12} weight="duotone" /> Government &amp; registration fees</div>
        <div className="grid grid-cols-2 gap-3">
          <NumField label="Min gov fees (USD)" value={form.gov_fees_min_usd} onChange={(v) => upd("gov_fees_min_usd", Number(v))} testid="wiz-gov-min" />
          <NumField label="Max gov fees (USD)" value={form.gov_fees_max_usd} onChange={(v) => upd("gov_fees_max_usd", Number(v))} testid="wiz-gov-max" />
        </div>
        <TextArea label="Gov fee note (e.g. 'varies by state')" value={form.gov_fees_note} onChange={(v) => upd("gov_fees_note", v)} testid="wiz-gov-note" rows={2} />
      </div>

      <div>
        <div className="overline mb-2">Tax &amp; ownership</div>
        <div className="grid grid-cols-3 gap-3">
          <NumField label="Corp tax %" value={form.corporate_tax_rate} onChange={(v) => upd("corporate_tax_rate", Number(v))} testid="wiz-corp-tax" />
          <NumField label="Personal tax %" value={form.personal_tax_rate} onChange={(v) => upd("personal_tax_rate", Number(v))} testid="wiz-pers-tax" />
          <NumField label="Foreign ownership %" value={form.foreign_ownership_pct} onChange={(v) => upd("foreign_ownership_pct", Number(v))} testid="wiz-foreign" />
        </div>
      </div>

      <div>
        <div className="overline mb-2 flex items-center gap-1"><ChartLineUp size={12} weight="duotone" /> Comparison metrics (1–10)</div>
        <div className="grid grid-cols-3 gap-3">
          <Slider label="Banking access" value={form.banking_access} onChange={(v) => upd("banking_access", v)} testid="wiz-banking-access" />
          <Slider label="Ease of business" value={form.ease_of_doing_business} onChange={(v) => upd("ease_of_doing_business", v)} testid="wiz-ease" />
          <Slider label="Startup ecosystem" value={form.startup_ecosystem} onChange={(v) => upd("startup_ecosystem", v)} testid="wiz-startup" />
          <Slider label="Tech sector" value={form.tech_sector} onChange={(v) => upd("tech_sector", v)} testid="wiz-tech" />
          <Slider label="Import / export" value={form.import_export} onChange={(v) => upd("import_export", v)} testid="wiz-trade" />
          <Slider label="Manufacturing" value={form.manufacturing} onChange={(v) => upd("manufacturing", v)} testid="wiz-mfg" />
          <Slider label="Govt incentives" value={form.government_incentives} onChange={(v) => upd("government_incentives", v)} testid="wiz-incentives" />
          <Slider label="Visa availability" value={form.visa_availability} onChange={(v) => upd("visa_availability", v)} testid="wiz-visa" />
          <Slider label="Immigration pathways" value={form.immigration_pathways} onChange={(v) => upd("immigration_pathways", v)} testid="wiz-immi" />
          <Slider label="Overall rating" value={form.overall_rating} onChange={(v) => upd("overall_rating", v)} testid="wiz-overall" />
        </div>
      </div>

      <div>
        <div className="overline mb-2 flex items-center gap-1"><MapPin size={12} weight="duotone" /> Tags &amp; lists</div>
        <ChipField label="Recommended industries" values={form.recommended_industries} onChange={(arr) => upd("recommended_industries", arr)} testid="wiz-industries" placeholder="Add an industry…" />
        <ChipField label="Common business types" values={form.common_business_types} onChange={(arr) => upd("common_business_types", arr)} testid="wiz-biz-types" placeholder="e.g. LLC, Branch, Sole Proprietorship" />
        <ChipField label="Expansion opportunities" values={form.expansion_opportunities} onChange={(arr) => upd("expansion_opportunities", arr)} testid="wiz-expansion" placeholder="e.g. EAC market access" />
        <TextArea label="Requirements summary (what's needed to register)" value={form.requirements_summary} onChange={(v) => upd("requirements_summary", v)} testid="wiz-requirements" rows={3} />
      </div>
    </div>
  );
}

function StepFaqs({ form, setForm }) {
  const update = (i, k, v) => setForm((f) => ({ ...f, faqs: f.faqs.map((x, idx) => idx === i ? { ...x, [k]: v } : x) }));
  const add = () => setForm((f) => ({ ...f, faqs: [...f.faqs, { q: "", a: "" }] }));
  const remove = (i) => setForm((f) => ({ ...f, faqs: f.faqs.filter((_, idx) => idx !== i) }));

  return (
    <div className="space-y-4">
      <p className="text-xs text-gos-slate">Add 2–4 frequently-asked questions. They&apos;ll render as an accordion at the bottom of the country page.</p>
      {form.faqs.map((faq, i) => (
        <div key={`faq-${i}`} className="bg-gos-subtle/40 border border-gos-border rounded-sm p-4 space-y-2" data-testid={`wiz-faq-${i}`}>
          <div className="flex items-center justify-between">
            <div className="overline">FAQ #{i + 1}</div>
            {form.faqs.length > 1 && (
              <button onClick={() => remove(i)} data-testid={`wiz-faq-${i}-remove`} className="text-rose-600 text-xs inline-flex items-center gap-1">
                <Trash size={12} /> Remove
              </button>
            )}
          </div>
          <Field label="Question" value={faq.q} onChange={(v) => update(i, "q", v)} testid={`wiz-faq-${i}-q`} placeholder="Can foreigners own 100% of a company?" />
          <TextArea label="Answer" value={faq.a} onChange={(v) => update(i, "a", v)} testid={`wiz-faq-${i}-a`} rows={2} placeholder="Yes — most sectors allow 100% foreign ownership…" />
        </div>
      ))}
      <button onClick={add} data-testid="wiz-faq-add" className="btn-secondary text-xs !py-2">
        <Plus size={12} weight="bold" /> Add another FAQ
      </button>
    </div>
  );
}

function StepReview({ form }) {
  const fee = 200;
  const minTotal = (form.gov_fees_min_usd || 0) + fee;
  const maxTotal = (form.gov_fees_max_usd || 0) + fee;
  return (
    <div className="space-y-5">
      <div className="bg-gos-subtle/40 border border-gos-border rounded-sm p-5">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-3xl">{form.flag}</span>
          <div>
            <div className="font-display text-2xl text-gos-charcoal">{form.name || "Untitled"}</div>
            <div className="text-xs text-gos-muted">/{form.slug} · {form.currency_code} · {form.capital_city || "—"}</div>
          </div>
        </div>
        {form.tagline && <p className="italic text-sm text-gos-slate">"{form.tagline}"</p>}
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm">
        <Detail label="Setup time" value={`${form.setup_timeline_days} days`} />
        <Detail label="Foreign ownership" value={`${form.foreign_ownership_pct}%`} />
        <Detail label="Corp tax" value={`${form.corporate_tax_rate}%`} />
        <Detail label="Overall rating" value={`${form.overall_rating}/10`} />
        <Detail label="Gov fees (USD)" value={`$${(form.gov_fees_min_usd || 0).toLocaleString()} – $${(form.gov_fees_max_usd || 0).toLocaleString()}`} />
        <Detail label="Est. total cost*" value={`$${minTotal.toLocaleString()} – $${maxTotal.toLocaleString()}`} />
      </div>
      <p className="text-[10px] text-gos-muted">* Government fees + USD $200 Open Business Anywhere service fee. Excludes add-on services.</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
        <TagList label="Recommended industries" items={form.recommended_industries} />
        <TagList label="Business types" items={form.common_business_types} />
        <TagList label="Expansion opportunities" items={form.expansion_opportunities} />
      </div>

      {form.faqs.filter((f) => f.q && f.a).length > 0 && (
        <div>
          <div className="overline mb-1">FAQs</div>
          <ul className="space-y-1 text-sm text-gos-slate">
            {form.faqs.filter((f) => f.q && f.a).map((f, i) => (
              <li key={`r-${i}`} className="flex gap-2"><span className="text-gos-sand">•</span> {f.q}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="border-t border-gos-border pt-4 text-xs text-gos-slate space-y-1">
        <div className="flex items-center gap-2"><Globe size={14} className="text-gos-navy" /> Will be live at <code className="bg-gos-subtle px-1 rounded-sm">/countries/{form.slug}</code> and <code className="bg-gos-subtle px-1 rounded-sm">/open-company-in-{form.slug}</code></div>
          <div className="flex items-center gap-2"><CheckCircle size={14} className="text-emerald-600" /> {form.active ? "Published immediately" : "Saved as draft (admin → Countries to activate)"}</div>
        <div className="flex items-center gap-2"><Sparkle size={14} className="text-gos-navy" /> Empty pricing rows auto-seeded for every service in {form.currency_code} — you can edit prices any time.</div>
      </div>
    </div>
  );
}

// --- small reusable bits ---
function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.18em] text-gos-muted mb-1">{label}</label>
      <input data-testid={testid} value={value || ""} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gos-border rounded-sm px-3 py-2 text-sm" />
    </div>
  );
}
function NumField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.18em] text-gos-muted mb-1">{label}</label>
      <input data-testid={testid} type="number" min="0" value={value || 0}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gos-border rounded-sm px-3 py-2 text-sm" />
    </div>
  );
}
function TextArea({ label, value, onChange, rows = 3, testid, placeholder }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.18em] text-gos-muted mb-1">{label}</label>
      <textarea data-testid={testid} value={value || ""} rows={rows} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gos-border rounded-sm px-3 py-2 text-sm" />
    </div>
  );
}
function Slider({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.18em] text-gos-muted mb-1 flex justify-between">
        <span>{label}</span><span className="text-gos-navy font-medium">{value}/10</span>
      </label>
      <input data-testid={testid} type="range" min="1" max="10" value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-gos-navy" />
    </div>
  );
}
function ChipField({ label, values, onChange, placeholder, testid }) {
  const [input, setInput] = useState("");
  const add = () => {
    const v = input.trim();
    if (!v || values.includes(v)) return;
    onChange([...values, v]);
    setInput("");
  };
  return (
    <div className="mb-3">
      <label className="block text-[10px] uppercase tracking-[0.18em] text-gos-muted mb-1">{label}</label>
      <div className="flex gap-2">
        <input data-testid={testid} value={input} placeholder={placeholder}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          className="flex-1 border border-gos-border rounded-sm px-3 py-2 text-sm" />
        <button type="button" onClick={add} data-testid={`${testid}-add`} className="btn-secondary text-xs !py-2"><Plus size={12} weight="bold" /></button>
      </div>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {values.map((v, i) => (
            <span key={`${v}-${i}`} className="bg-gos-navy/10 text-gos-navy text-xs px-2 py-0.5 rounded-sm flex items-center gap-1">
              {v}
              <button type="button" onClick={() => onChange(values.filter((_, idx) => idx !== i))} className="hover:text-rose-600">×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
function Detail({ label, value }) {
  return (
    <div className="bg-white border border-gos-border rounded-sm p-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-gos-muted">{label}</div>
      <div className="text-gos-charcoal mt-0.5">{value}</div>
    </div>
  );
}
function TagList({ label, items }) {
  if (!items?.length) return null;
  return (
    <div>
      <div className="overline mb-1">{label}</div>
      <div className="flex flex-wrap gap-1">
        {items.map((t) => <span key={t} className="bg-gos-subtle text-gos-charcoal px-2 py-0.5 rounded-sm">{t}</span>)}
      </div>
    </div>
  );
}
